import React, { useEffect, useCallback } from 'react';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';

import { noop } from '@ajaibid/utils';
import { Input } from '@ajaibid/components';
import useField from './hooks/useField';

const FormSelect = (props) => {
  const {
    id,
    name,
    defaultValue,
    error: propsError,
    onChange,
    onClick,
    onError,
    onSearchChange,
    onValid,
    validators,
    ...baseProps
  } = props;

  const [
    fieldValue,
    { updateField, updateForm, error: errorMessage, errorKey, showError, setShowError },
  ] = useField(name, { defaultValue, validators });

  const onChangeCb = useCallback((params) => onChange(params), [onChange]);
  const onErrorCb = useCallback((params) => onError(params), [onError]);
  const onValidCb = useCallback((params) => onValid(params), [onValid]);

  useEffect(() => {
    if (errorMessage) {
      onErrorCb({ errorMessage, errorKey });
    } else {
      onValidCb();
    }
  }, [errorMessage, errorKey, onErrorCb, onValidCb]);

  // TODO: this line breaks validator. Wait for QA regression, if it works fine without it, remove
  // useEffect(() => {
  //   if (propsError) {
  //     updateForm(name, fieldValue, propsError);
  //   }
  // }, [propsError, name, updateForm]);

  const handleChange = (option) => {
    onChangeCb(option);
    updateField(option);
  };

  return (
    <Input.Select
      id={id}
      name={name}
      value={fieldValue}
      onChange={handleChange}
      onClick={onClick}
      onSearchChange={onSearchChange}
      onBlur={() => !showError && setShowError(true)}
      // Only pass the error message when Form showError is true.
      error={showError ? propsError || errorMessage || '' : ''}
      {...baseProps}
    />
  );
};

const optionPropType = oneOfType([
  string,
  shape({
    description: string,
    disabled: bool,
    icon: string,
    text: string,
    value: oneOfType([string, number]),
  }),
]);

FormSelect.propTypes = {
  // <Input.Select> props
  id: string.isRequired,
  name: string.isRequired,
  className: string,
  defaultValue: optionPropType,
  disabled: bool,
  icon: node,
  maxOptions: number,
  options: arrayOf(optionPropType),
  placeholder: string,
  placeholderSearch: string,
  style: object,
  textHint: string,
  variant: oneOf(['default', 'radio']),
  withSearch: bool,
  allowCustomOnSearchNotFound: bool,
  // <Form.Select> props
  onChange: func,
  onClick: func,
  onError: func,
  onSearchChange: func,
  onValid: func,
  error: string,
  validators: objectOf(
    shape({
      validator: func,
      message: string,
    })
  ),
};

FormSelect.defaultProps = {
  // <Input.Select> props
  className: '',
  defaultValue: '',
  disabled: false,
  icon: null,
  maxOptions: null,
  options: [],
  placeholder: '',
  placeholderSearch: '',
  style: {},
  textHint: '',
  variant: 'default',
  withSearch: false,
  allowCustomOnSearchNotFound: false,
  // <Form.Select> props
  onChange: noop,
  onClick: noop,
  onError: noop,
  onSearchChange: noop,
  onValid: noop,
  error: '',
  validators: {},
};

export default FormSelect;
